import React from "react";
import { Box, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { alpha, useTheme } from "@mui/material/styles";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const AssistantWelcome = ({ message, onClose, title = "Olá!" }) => {
  const theme = useTheme();

  return (
    <Box sx={{ p: 3 }}>
      {/* Glass gradient banner */}
      <Box
        sx={{
          p: 3,
          mb: 3,
          borderRadius: "16px",
          position: "relative",
          overflow: "hidden",
          background: `
            linear-gradient(
              135deg,
              ${theme.palette.primary.dark} 0%,
              ${theme.palette.primary.main} 30%,
              ${theme.palette.info.main} 60%,
              ${alpha(theme.palette.success.main, 0.7)} 100%
            )
          `,
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background: `
              radial-gradient(ellipse 80% 60% at 20% 80%, ${alpha(theme.palette.primary.light, 0.4)} 0%, transparent 70%),
              radial-gradient(ellipse 60% 50% at 80% 20%, ${alpha(theme.palette.info.light, 0.3)} 0%, transparent 70%)
            `,
          },
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            backdropFilter: "blur(40px) saturate(1.2)",
            background: alpha("#fff", 0.05),
            borderRadius: "inherit",
          },
        }}
      >
        {/* Floating orbs */}
        <Box
          sx={{
            position: "absolute",
            top: -30,
            right: -10,
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha("#fff", 0.1)} 0%, transparent 70%)`,
            filter: "blur(2px)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -20,
            left: 30,
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(theme.palette.info.light, 0.15)} 0%, transparent 70%)`,
            filter: "blur(4px)",
          }}
        />

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            gap: 2.5,
          }}
        >
          {/* Glass icon */}
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "14px",
              bgcolor: alpha("#fff", 0.12),
              border: `1px solid ${alpha("#fff", 0.2)}`,
              backdropFilter: "blur(12px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: `0 8px 32px ${alpha("#000", 0.12)}`,
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 24, color: "#fff" }} />
          </Box>

          <Box>
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 700,
                fontSize: "1.25rem",
                lineHeight: 1.3,
                mb: 0.25,
                textShadow: `0 1px 8px ${alpha("#000", 0.15)}`,
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: alpha("#fff", 0.85),
                lineHeight: 1.5,
                textShadow: `0 1px 4px ${alpha("#000", 0.1)}`,
              }}
            >
              Bem-vindo ao Slapie
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Message body */}
      <Box
        sx={{
          p: 2.5,
          borderRadius: "12px",
          bgcolor: alpha(theme.palette.grey[100], 0.6),
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          mb: 3,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.primary,
            lineHeight: 1.7,
            fontWeight: 450,
          }}
        >
          {message}
        </Typography>
      </Box>

      {/* CTA */}
      <Button
        variant="contained"
        fullWidth
        size="large"
        onClick={onClose}
        endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
        sx={{
          py: 1.5,
          borderRadius: "12px",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.95rem",
          bgcolor: theme.palette.primary.main,
          boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
          "&:hover": {
            bgcolor: theme.palette.primary.dark,
            boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.45)}`,
          },
        }}
      >
        Vamos lá!
      </Button>
    </Box>
  );
};

export default AssistantWelcome;
